/**
 * Hidden column / series keys for a report view (WSRADM-0051).
 *
 * Source of truth is the query param `param` (comma-joined keys). When the param
 * is absent, the `wsr_admin_views` preference cookie seeds it once on mount
 * (replace) — an explicit URL always wins, and an explicit empty `?hide=` means
 * "show all". Toggles push history so Back undoes them. Hiding is display only:
 * nothing here computes or relabels a total.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { joinViewKeys, readViewPref, splitViewKeys, writeViewPref } from '@/config/viewPrefs';

export interface UseHiddenKeysOptions {
  /** Query param holding the hidden keys (e.g. `hide`). */
  param: string;
  /** Surface entry in the `wsr_admin_views` cookie (e.g. `membership`). */
  prefsKey: string;
  /** Keys the current view can show; others in the URL are ignored. */
  available: readonly string[];
  /**
   * Seed the param from the cookie on mount (default true). A page that seeds
   * several params in one pass (the dashboard) passes false and calls
   * `seedHiddenParam` itself so two replace-navigations never race.
   */
  seed?: boolean;
}

export interface HiddenKeys {
  hidden: ReadonlySet<string>;
  toggle: (key: string) => void;
  showAll: () => void;
  hiddenCount: number;
}

/**
 * Set `param` from the cookie when the URL does not carry it. Returns true when
 * `params` changed. Mutates `params`.
 */
export function seedHiddenParam(params: URLSearchParams, param: string, prefsKey: string): boolean {
  if (params.has(param)) return false;
  const remembered = readViewPref(prefsKey);
  if (remembered === null || remembered.length === 0) return false;
  params.set(param, joinViewKeys(remembered));
  return true;
}

export function useHiddenKeys({
  param,
  prefsKey,
  available,
  seed = true,
}: UseHiddenKeysOptions): HiddenKeys {
  const [searchParams, setSearchParams] = useSearchParams();
  const seededRef = useRef(false);

  const raw = searchParams.get(param);
  // Until the seed lands, read the cookie directly so the first paint matches.
  const requested = useMemo(
    () => (raw !== null ? splitViewKeys(raw) : (readViewPref(prefsKey) ?? [])),
    [raw, prefsKey],
  );

  const availableKey = available.join('\u0000');
  const availableSet = useMemo(
    () => new Set(availableKey.length > 0 ? availableKey.split('\u0000') : []),
    [availableKey],
  );

  const hidden = useMemo(() => {
    const next = new Set<string>();
    for (const key of requested) {
      if (availableSet.has(key)) next.add(key);
    }
    // All-hidden is impossible: a hand-edited URL hiding everything shows everything.
    if (next.size > 0 && next.size >= availableSet.size) return new Set<string>();
    return next;
  }, [requested, availableSet]);

  useEffect(() => {
    if (!seed || seededRef.current) return;
    seededRef.current = true;
    const next = new URLSearchParams(searchParams);
    if (seedHiddenParam(next, param, prefsKey)) setSearchParams(next, { replace: true });
  }, [seed, searchParams, setSearchParams, param, prefsKey]);

  const write = (keys: readonly string[]) => {
    const next = new URLSearchParams(searchParams);
    next.set(param, joinViewKeys(keys));
    setSearchParams(next);
    writeViewPref(prefsKey, keys);
  };

  const toggle = (key: string) => {
    if (!availableSet.has(key)) return;
    // Stale keys (not in this view) stay; available keys are rebuilt from `hidden`.
    const stale = requested.filter((k) => !availableSet.has(k));
    if (hidden.has(key)) {
      write([...stale, ...[...hidden].filter((k) => k !== key)]);
      return;
    }
    // Guard: the last visible key never hides.
    if (hidden.size + 1 >= availableSet.size) return;
    write([...stale, ...hidden, key]);
  };

  const showAll = () => {
    const next = new URLSearchParams(searchParams);
    next.set(param, '');
    setSearchParams(next);
    writeViewPref(prefsKey, []);
  };

  return { hidden, toggle, showAll, hiddenCount: hidden.size };
}
